import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { searchPeople } from './api';
import type { OrgFormValue, OrgsMeta, PersonOption } from './types';
import { fieldClass, helperClass, labelClass } from '../styles';

interface OrgFormFieldsProps {
  meta: OrgsMeta;
  value: OrgFormValue;
  onChange: (next: OrgFormValue) => void;
  mode: 'create' | 'edit';
  disabled?: boolean;
}

const OrgFormFields: React.FC<OrgFormFieldsProps> = ({
  meta,
  value,
  onChange,
  mode,
  disabled,
}) => {
  const set = (patch: Partial<OrgFormValue>) => onChange({ ...value, ...patch });
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PersonOption[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    const handle = window.setTimeout(async () => {
      setSearching(true);
      try {
        setResults(await searchPeople(query.trim()));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => window.clearTimeout(handle);
  }, [query]);

  const selectedAdmin =
    value.org_admin_id !== ''
      ? meta.account_managers.find((p) => p.id === value.org_admin_id) || {
          id: value.org_admin_id,
          name: null,
          email: value.org_admin_email,
        }
      : null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <label className={labelClass}>
          Name <span className="text-red-600">*</span>
        </label>
        <input
          value={value.name}
          disabled={disabled}
          onChange={(e) => set({ name: e.target.value })}
          className={fieldClass}
        />
      </div>
      <div>
        <label className={labelClass}>
          Tier <span className="text-red-600">*</span>
        </label>
        <select
          value={value.tier_id}
          disabled={disabled}
          onChange={(e) => set({ tier_id: e.target.value ? Number(e.target.value) : '' })}
          className={fieldClass}
        >
          <option value="">Select a tier</option>
          {meta.tiers.map((tier) => (
            <option key={tier.id} value={tier.id}>
              {tier.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelClass}>
          Currency <span className="text-red-600">*</span>
        </label>
        <select
          value={value.currency_id}
          disabled={disabled}
          onChange={(e) => set({ currency_id: e.target.value ? Number(e.target.value) : '' })}
          className={fieldClass}
        >
          <option value="">Select a currency</option>
          {meta.currencies.map((currency) => (
            <option key={currency.id} value={currency.id}>
              {currency.code} — {currency.name}
            </option>
          ))}
        </select>
      </div>
      <div className="md:col-span-2">
        <label className={labelClass}>
          Account manager <span className="text-red-600">*</span>
        </label>
        <select
          value={value.account_manager_id}
          disabled={disabled}
          onChange={(e) =>
            set({ account_manager_id: e.target.value ? Number(e.target.value) : '' })
          }
          className={fieldClass}
        >
          <option value="">Select an account manager</option>
          {meta.account_managers.map((person) => (
            <option key={person.id} value={person.id}>
              {person.name || person.email} ({person.email})
            </option>
          ))}
        </select>
        <p className={helperClass}>
          Catalyx users with the organizations_account_manager capability. If you are not the
          account manager, both of you receive membership in the organization.
        </p>
      </div>
      <div className="md:col-span-2">
        <label className={labelClass}>Organization admin</label>
        {selectedAdmin && value.org_admin_id !== '' ? (
          <div className="flex items-center gap-2">
            <div className={`${fieldClass} flex-1`}>
              {selectedAdmin.name || selectedAdmin.email || `User #${value.org_admin_id}`}
            </div>
            <button
              type="button"
              disabled={disabled}
              onClick={() => set({ org_admin_id: '', org_admin_email: '' })}
              className="p-2 text-[#626262] hover:text-red-700"
              aria-label="Clear organization admin"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <input
              value={query || value.org_admin_email}
              disabled={disabled}
              onChange={(e) => {
                setQuery(e.target.value);
                set({ org_admin_email: e.target.value, org_admin_id: '' });
              }}
              placeholder="Search by name or enter an email"
              className={fieldClass}
            />
            {searching && <p className={helperClass}>Searching…</p>}
            {results.length > 0 && (
              <ul className="mt-1 border border-[#E8E8E8] rounded-md max-h-40 overflow-y-auto">
                {results.map((person) => (
                  <li key={person.id}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[#F5F5F5]"
                      onClick={() => {
                        set({
                          org_admin_id: person.id,
                          org_admin_email: person.email,
                        });
                        setQuery('');
                        setResults([]);
                      }}
                    >
                      {person.name || person.email}
                      <span className="text-[#8A8A8A]"> · {person.email}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        <p className={helperClass}>
          Optional. If the person does not exist yet, enter their email and a passwordless account
          will be created.
        </p>
      </div>
      {mode === 'edit' && (
        <div>
          <label className={labelClass}>Active</label>
          <label className="inline-flex items-center gap-2 text-sm text-[#4A4A4A]">
            <input
              type="checkbox"
              checked={value.is_active}
              disabled={disabled}
              onChange={(e) => set({ is_active: e.target.checked })}
            />
            Organization is active
          </label>
        </div>
      )}
    </div>
  );
};

export default OrgFormFields;
